import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../../../../../libs/core';
import { PortalAuthFlowService } from './portal-auth-flow.service';

@Injectable({ providedIn: 'root' })
export class PortalAuthSessionService {
  private readonly LAST_ROUTE_KEY = 'portal.last-protected-route';
  private readonly DEFAULT_ROUTE = '/dashboard';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly flowService = inject(PortalAuthFlowService);

  private logoutInFlight = false;
  private sessionGeneration = 0;

  getSessionGeneration(): number {
    return this.sessionGeneration;
  }

  isAuthenticated(): boolean {
    return this.authService.isAuthenticated() && this.authService.hasRole('customer');
  }

  logout(): void {
    if (this.logoutInFlight) return;
    this.logoutInFlight = true;
    this.sessionGeneration++;
    this.clearSessionArtifacts();
    void this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  resetLogoutLock(): void {
    this.logoutInFlight = false;
  }

  setLastProtectedRoute(url: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.isProtectedRoute(url)) return;
    localStorage.setItem(this.LAST_ROUTE_KEY, url);
  }

  getLastProtectedRoute(): string {
    if (!isPlatformBrowser(this.platformId)) return this.DEFAULT_ROUTE;
    const saved = localStorage.getItem(this.LAST_ROUTE_KEY);
    if (!saved || !this.isProtectedRoute(saved)) return this.DEFAULT_ROUTE;
    return saved;
  }

  clearLastProtectedRoute(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.removeItem(this.LAST_ROUTE_KEY);
  }

  isProtectedRoute(url: string): boolean {
    const path = `/${String(url || '').replace(/^\/+/, '')}`;
    if (!path || path === '/') return false;
    const disallowed = [
      '/login',
      '/verify',
      '/forgot-password',
      '/set-password',
      '/reset-password',
      '/error',
    ];
    return !disallowed.some((p) => path === p || path.startsWith(`${p}/`));
  }

  private clearSessionArtifacts(): void {
    this.flowService.clearChallengeContext();
    this.flowService.clearPersistLogin();
    this.clearLastProtectedRoute();
    this.authService.logout();
  }
}
